import { createTheme, type ThemeOptions, type Shadows } from "@mui/material";

import primitives from "./primitives";
import semantics from "./semantics";

const sharedTheme: Partial<ThemeOptions> = {
  shadows: Array.from({ length: 25 }).fill("none") as Shadows,

  fontSizes: { ...semantics.typography.size },
  typography: {
    fontFamily: "Inter",
    fontWeightLight: semantics.typography.weight.regular,
    fontWeightRegular: semantics.typography.weight.medium,
    fontWeightMedium: semantics.typography.weight.semibold,
    fontWeightBold: semantics.typography.weight.extrabold,

    h1: { fontWeight: semantics.typography.weight.semibold, fontSize: "3.4rem", lineHeight: 1.1 },
    h2: { fontWeight: semantics.typography.weight.semibold, fontSize: "2.3rem", lineHeight: 1.1 },
    h3: { fontWeight: semantics.typography.weight.semibold, fontSize: "1.3rem", lineHeight: 1.1 },
    h4: { fontWeight: semantics.typography.weight.semibold, fontSize: "1.2rem", lineHeight: 1.1 },
    h5: { fontWeight: semantics.typography.weight.semibold, fontSize: "1.2rem", lineHeight: 1.1 },
    h6: { fontWeight: semantics.typography.weight.semibold, fontSize: "1.1rem", lineHeight: 1.1 },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ".echarts-for-react": {
          height: "100% !important",
          width: "100% !important",
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          width: "100% !important",
          height: "100% !important",
          backgroundImage: `url("../src/assets/images/chart_skeleton.svg") !important`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          transform: "scale(1, 1) !important",
        },
        wave: {
          "&::after": {
            background: `linear-gradient(90deg, transparent, ${semantics.map.secondary}, transparent)`,
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          ".MuiAlert-root &": {
            color: "inherit",
          },
        }),
      },
    },

    MuiSelect: {
      variants: [
        {
          props: { size: "small" },
          style: ({ theme }) => ({
            width: 140,
            height: 24,
            p: 0,
            fontSize: theme.fontSizes.sm,
            fontWeight: theme.typography.fontWeightRegular,
          }),
        },
        {
          props: { size: "medium" },
          style: ({ theme }) => ({
            width: 120,
            height: 36,
            p: 0,
            my: 0,
            mx: 1.25,
            fontSize: theme.fontSizes.sm,
            fontWeight: theme.typography.fontWeightRegular,
          }),
        },
      ],
    },

    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: theme.fontSizes.sm,
          fontWeight: theme.typography.fontWeightRegular,
          fontFamily: theme.typography.fontFamily,
        }),
      },
      variants: [
        {
          props: { className: "medium" },
          style: ({ theme }) => ({
            fontSize: theme.fontSizes.md,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
          }),
        },
        {
          props: { className: "large" },
          style: ({ theme }) => ({
            fontSize: theme.fontSizes.lg,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
          }),
        },
      ],
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          "& .MuiDataGrid-row": {
            borderBottom: `1px solid ${semantics.border.default}`,
          },
          "& .MuiDataGrid-row:last-child": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnSeparator": {
            color: `${semantics.border.default}`,
          },
          "& .MuiDataGrid-columnHeaders": {
            borderBottom: `1px solid ${semantics.border.default}`,
          },
          "& .MuiDataGrid-footerContainer": { borderTop: "none" },
          "& .MuiDataGrid-scrollbar": { overflow: "hidden" },
          "& .MuiDataGrid-row:hover": { cursor: "pointer" },
          "& .MuiDataGrid-cell:focus-within": { outline: "none !important" },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: semantics.radius.md,
          "&.MuiPickerPopper-paper": {
            border: "1px solid",
            borderColor: semantics.border.default,
          },
          "& .MuiDayCalendar-weekDayLabel": {
            fontSize: semantics.typography.size.lg,
          },
          "& .MuiPickersLayout-root": {
            display: "block",
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: semantics.radius.full,
          fontSize: semantics.typography.size.sm,
          fontWeight: semantics.typography.weight.semibold,
        },
        icon: { fontSize: semantics.typography.size.sm },
      },
      defaultProps: {
        sx: {
          my: "auto",
          mx: 1,
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            borderRight: "4px solid",
            borderColor: semantics.brand.primary,
          },
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: primitives.typography.size.lg,
          "&:last-child": { padding: 16 },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          border: "none",
          fontSize: primitives.typography.size.sm,
        },
      },
    },

    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontWeight: semantics.typography.weight.semibold,
          fontSize: primitives.typography.size.sm,
        },
      },
    },

    MuiList: { defaultProps: { disablePadding: true } },
    MuiListItem: { defaultProps: { disablePadding: true } },
  },
};

export const theme = createTheme({
  ...sharedTheme,

  palette: {
    mode: "light",
    primary: { ...semantics.primary },
    secondary: {
      ...semantics.secondary,
    },

    success: {
      ...semantics.success,
    },

    error: {
      ...semantics.error,
    },

    warning: { ...semantics.warning },
    info: { ...semantics.info },

    action: {
      hover: semantics.surface.overlay,
    },

    background: {
      default: semantics.surface.page,
      paper: semantics.surface.card,
    },

    text: {
      primary: semantics.text.primary,
      secondary: semantics.text.secondary,
      disabled: semantics.text.placeholder,
    },

    divider: semantics.border.default,

    brand: semantics.brand,
    positive: semantics.positive,
    negative: semantics.negative,
    surface: semantics.surface,
    border: semantics.border,
    map: semantics.map,
    levelColors: semantics.levelColors,
    changeMarkers: semantics.changeMarkers,
    transparent: semantics.transparent,
  },
  radius: {
    ...semantics.radius,
  },
});
